import React, { useState, useContext, useEffect } from 'react';
import cn from 'classnames';
import ReactHintFactory from 'react-hint';
import Slider from 'react-slick';
import Select from 'react-select';
import TailoredPricingSVG from 'public/images/svg/tailored-pricing.svg';
import Image from 'next/image';
import { PricingRegion } from '../utils/pricing/PricingRegion';
import { TimelyPlanName } from '../utils/pricing/TimelyPlanName';
import { getFixedMonthlyPrice, getRegionPriceInfo, getPlanMonthlyPrice } from '../utils/pricing/calculator';
import { PageContext } from '../context/page-context';
import { pricingFeatures } from 'components/utils/pricing-features';
import { MediaServerRender, ProvideMediaMatchers, MediaMatcher } from 'react-media-match';

const ReactHint = ReactHintFactory(React);

const country = [
    { value: [PricingRegion.UNITED_STATES], label: 'United States' },
    { value: [PricingRegion.NEW_ZEALAND], label: 'New Zealand (excludes GST)' },
    { value: [PricingRegion.AUSTRALIA], label: 'Australia (excludes GST)' },
    { value: [PricingRegion.GREAT_BRITAIN], label: 'Great Britain' },
    { value: [PricingRegion.CANADA], label: 'Canada' },
    { value: [PricingRegion.REST_OF_THE_WORLD], label: 'Rest of the world' },
];

export default function PlanCalculator() {
    const context = useContext(PageContext);
    // const { geolocation, setPageContext, businessSize, planRate } = context;
    const { geolocation, setPageContext, businessSize } = context;

    const [staffCount, setCount] = useState(1);
    const [defaultCountry, setCountry] = useState(country[0]);
    const pricingFrequency = 'monthly';

    useEffect(() => {

        let defaultStaffCount = 1;
        if (businessSize && typeof context.staffCount === 'undefined') {
            if (businessSize == '2') {
                defaultStaffCount = 2;
            }
            if (businessSize == '3') {
                defaultStaffCount = 8;
            }
            if (businessSize == '4') {
                defaultStaffCount = 20;
            }
        }

        if (context.staffCount) {
            defaultStaffCount = context.staffCount;
        }

        if (window && window.location.pathname) {
            if (window.location.pathname.includes('solo')) {
                defaultStaffCount = 1;
            }
            if (window.location.pathname.includes('small')) {
                defaultStaffCount = 2;
            }
            if (window.location.pathname.includes('large')) {
                defaultStaffCount = 8;
            }
            if (window.location.pathname.includes('enterprise')) {
                defaultStaffCount = 20;
            }
        }

        setCount(defaultStaffCount);

        if (context.geolocation) {
            selectCountryToSet(context.geolocation);
        }
    }, [businessSize]);

    const selectCountryToSet = (newCountryCode) => {
        // I'm not sure why, but sometimes it's coming through as type `[string]` instead of `string`
        if (Array.isArray(newCountryCode)) {
            newCountryCode = newCountryCode[0];
        }

        country.map(c => {
            if (c.value == newCountryCode) {
                setCountry(c);
                if (context.geolocation !== newCountryCode) {
                    setPageContext('geolocation', newCountryCode, {
                        ...context,
                    });
                }
            }
        });
    };

    const setStaffCount = val => {
        let businessS = 1;
        if (parseInt(val, 10) > 1) {
            businessS = 2;
        }
        if (parseInt(val, 10) > 7) {
            businessS = 3;
        }
        if (parseInt(val, 10) > 19) {
            businessS = 4;
        }
        setCount(val);
        context.staffCount = val;
        setPageContext('businessSize', businessS, { ...context });
        setPageContext('staffCount', val, { ...context });
    };

    return (
        <div className='PlanCalculator-wrap'>
            <div className="container">
                <div className="PricingCard-heading">
                    <h1>Get a price for</h1>
                    <div className="col-sm-6 col-md-5">
                        <StaffSelector
                            staffCount={staffCount}
                            setStaffCount={setStaffCount}
                        />
                    </div>
                </div>
            </div>

            <div className="container container--fullHeight">
                <ProvideMediaMatchers>
                    <MediaServerRender predicted="desktop" hydrated={true}>
                        <MediaMatcher
                            mobile={
                                <PlanCarousel
                                    geolocation={geolocation}
                                    staffCount={staffCount}
                                    planType={pricingFrequency}
                                />
                            }
                            tablet={
                                <PlanList
                                    geolocation={geolocation}
                                    staffCount={staffCount}
                                    planType={pricingFrequency}
                                />
                            }
                        />
                    </MediaServerRender>
                </ProvideMediaMatchers>
            </div>
            <div className="container u-vSpacingSM-top">
                <div className="col-xs-12 col-sm-2 col-sm-offset-5 u-textCenter">
                    <p>
                        <Select
                            className="PlanCalculator-select PlanCalculator-select--small"
                            classNamePrefix="PlanCalculator-select"
                            maxMenuHeight={500}
                            theme={theme => ({
                                ...theme,
                                colors: {
                                    ...theme.colors,
                                    primary75: '#fc5f64',
                                    primary50: '#fc5f64',
                                },
                            })}
                            isSearchable={false}
                            onChange={e => {
                                selectCountryToSet(e.value);
                            }}
                            defaultValue={defaultCountry}
                            value={defaultCountry}
                            name="country"
                            options={country}
                        />
                    </p>
                </div>
                <div className="col-xs-12 u-textCenter">
                    <p>
                        If you have any questions, including how to set-up
                        part-time staff, then
                        <button className="Button btn-nav-call Button--link Button--plain">
                            give us a call
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}

function PlanList({ geolocation, staffCount, planType }) {
    return (
        <>
            <PricingCard
                type={TimelyPlanName.BUILD}
                geolocation={geolocation}
                name="Build"
                description="Make business easy with the foundations for managing."
                staffCount={staffCount}
                planType={planType}
            />
            <PricingCard
                type={TimelyPlanName.ELEVATE}
                geolocation={geolocation}
                name="Elevate"
                description="Grow your revenue and elevate your brand."
                staffCount={staffCount}
                planType={planType}
            />
            <PricingCard
                type={TimelyPlanName.INNOVATE}
                geolocation={geolocation}
                description="Access sophisticated tools to innovate and grow."
                name="Innovate"
                staffCount={staffCount}
                planType={planType}
            />
        </>
    );
}
function PlanCarousel({ geolocation, staffCount, planType }) {
    const settings = {
        dots: true,
        infinite: false,
        initialSlide: 2,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
    };
    return (
        <Slider {...settings}>
            <PricingCard
                type={TimelyPlanName.BUILD}
                geolocation={geolocation}
                name="Build"
                description="Make business easy with the foundations for managing."
                staffCount={staffCount}
                planType={planType}
            />
            <PricingCard
                type={TimelyPlanName.ELEVATE}
                geolocation={geolocation}
                name="Elevate"
                description="Grow your revenue and elevate your brand."
                staffCount={staffCount}
                planType={planType}
            />
            <PricingCard
                type={TimelyPlanName.INNOVATE}
                geolocation={geolocation}
                description="Access sophisticated tools to innovate and grow."
                name="Innovate"
                staffCount={staffCount}
                planType={planType}
            />
        </Slider>
    );
}

function PricingCard({
    geolocation,
    staffCount,
    name,
    description,
    type,
}) {
    const regionPriceInfo = getRegionPriceInfo(geolocation);
    const { currencyCode, sign } = regionPriceInfo;
    const hasFixedPriceOffering = !!getFixedMonthlyPrice(geolocation, type);
    const hasCustomPackageOffering = regionPriceInfo.prices.customPackages[type];

    let totalPrice = getPlanMonthlyPrice(geolocation, type, staffCount);

    if (staffCount >= 20) {
        if (hasCustomPackageOffering) {
            return (
                <div className={`col-sm-4`}>
                    <div className={`PricingCard PricingCard--${type}`}>
                        <h3 className="PricingCard-title">
                            {name}
                            {type === TimelyPlanName.ELEVATE && (
                                <span className="Badge Badge--mostPopular">
                                    <span className="fa fa-star"></span><span className="u-md-only">P</span><span className="u-md-hidden">Most p</span>opular
                                </span>
                            )}
                        </h3>
                        <p className="PricingCard-description">{description}</p>
                        <Image layout="fixed" width={43} height={53} src={TailoredPricingSVG} />
                        <div className="PricingCard-staffPrice">
                            We’ll craft a package to suit you
                        </div>
                        <button
                            className={cn(
                                'btn-nav-call Button Button--full',
                                { 'Button--red': type === 'innovate' },
                                { 'Button--primary': type === TimelyPlanName.ELEVATE }
                            )}>
                            Book a call
                        </button>

                        <ListFeatures currencyCode={currencyCode} staffCount={staffCount} type={type} />
                    </div>
                </div>
            );
        } else if (hasFixedPriceOffering) {
            return (
                <div className={`col-sm-4`}>
                    <div className={`PricingCard PricingCard--disabled`}>
                        <h3>{name}</h3>
                        <p className="PricingCard-description">
                            Custom packages are not available on this plan.
                        </p>
                    </div>
                </div>
            );
        }
    }

    if (staffCount > 7 && !hasFixedPriceOffering) {
        return (
            <div className={`col-sm-4`}>
                <div className={`PricingCard PricingCard--disabled`}>
                    <h3>{name}</h3>
                    <p className="PricingCard-description">
                        This plan is only available for businesses with 1-7
                        staff.
                    </p>
                </div>
            </div>
        );
    }

    let smsRate = {
        [TimelyPlanName.BUILD]: 100,
        [TimelyPlanName.ELEVATE]: 200,
        [TimelyPlanName.INNOVATE]: 300,
    };
    if (currencyCode === 'NZD') {
        smsRate = {
            [TimelyPlanName.BUILD]: 100,
            [TimelyPlanName.ELEVATE]: 150,
            [TimelyPlanName.INNOVATE]: 200,
        };
    }
    const numSMS = smsRate[type] * staffCount;
    let smsDescription = '%sms% SMS per month included'.replace('%sms%', numSMS);
    if (staffCount > 7) {
        smsDescription = `${smsRate[type]} SMS per staff per month`;
    }

    return (
        <div className={`col-sm-4`}>
            <div className={cn(
                `PricingCard PricingCard--${type}`,
            )}>
                <h3 className="PricingCard-title">
                    {name}
                    {type === TimelyPlanName.ELEVATE && (
                        <span className="Badge Badge--mostPopular">
                            <span className="fa fa-star"></span><span className="u-md-visible">P</span><span className="u-md-hidden">Most p</span>opular
                        </span>
                    )}
                </h3>
                <p className="PricingCard-description">{description}</p>
                <div className="PricingCard-price">
                    <div className="Pricing-price PricingCard-priceBlock">
                        <div className="Pricing-symbol">{sign}</div>
                        <div className="Pricing-cost">
                            <div className="Pricing-amount">{totalPrice}</div>
                        </div>
                    </div>
                    <div className="Pricing-recurrence">
                        <span className="Pricing-currency">{currencyCode}</span>
                        {' / '}
                        <span className="Pricing-recurrence-amount">
                            {'mo'}
                        </span>
                    </div>
                </div>
                <span className="PricingCard-sub-line">plus any <a href="/timelypay/#block_64a21041f706d" target="_blank">transaction fees</a></span>
                <div className="Pricing-smsQuota u-textCenter"><div className="Pricing-smsQuota-text">{smsDescription}</div></div>
                <a
                    // href={`/sweet-sign-up/`}
                    href='https://app.gettimely.com/register'
                    className={cn(
                        'Button Button--full',
                        { 'Button--red': type !== TimelyPlanName.ELEVATE },
                        { 'Button--primary': type === TimelyPlanName.ELEVATE }
                    )}>
                    Start a free trial
                </a>

                <hr />
                <ListFeatures currencyCode={currencyCode} staffCount={staffCount} type={type} />
                <p
                    className={cn('PricingCard-seeFeature', {
                        'u-visibilityHidden': type === TimelyPlanName.BASIC,
                    })}>
                    <a
                        href="#featureList"
                        className="Button Button--link-coral">
                        See all features
                    </a>
                </p>
            </div>
        </div>
    );
}
function ListFeatures({ type, staffCount, currencyCode }) {
    return (
        <ul className="Pricing-MajorFeatures">
            {pricingFeatures.map((f, k) => {
                // if f.name includes %sms% replace it with number of sms messages
                let name = f.name;
                if (f.name.indexOf('%sms%') !== -1) {
                    // let sms =
                    let smsRate = {
                        [TimelyPlanName.BUILD]: 100,
                        [TimelyPlanName.ELEVATE]: 200,
                        [TimelyPlanName.INNOVATE]: 300,
                    };
                    if (currencyCode === 'NZD') {
                        smsRate = {
                            [TimelyPlanName.BUILD]: 100,
                            [TimelyPlanName.ELEVATE]: 150,
                            [TimelyPlanName.INNOVATE]: 200,
                        };
                    }
                    const numSMS = smsRate[type] * staffCount;
                    name = name.replace('%sms%', numSMS);
                    if (staffCount > 7) {
                        name = `${smsRate[type]} per bookable staff per month`;
                    }
                }
                if (f.hide && f.hide.includes(type)) {
                    return null;
                }

                if (f.available.includes(type)) {
                    return (
                        <li
                            className="Pricing-MajorFeature--tick Pricing-MajorFeature"
                            key={k}>
                            <span
                                className="Tooltip-trigger--link"
                                data-plan={f.description}>
                                {name}
                            </span>
                        </li>
                    );
                } else {
                    return (
                        <li
                            className="Pricing-MajorFeature--dash Pricing-MajorFeature"
                            key={k}>
                            <span>{name}</span>
                        </li>
                    );
                }
            })}
        </ul>
    );
}

function StaffSelector({ staffCount, setStaffCount }) {
    const position = 'top';
    const options = [
        { value: '1', label: 'Just me' },
        { value: '2', label: '2 bookable staff' },
        { value: '3', label: '3 bookable staff' },
        { value: '4', label: '4 bookable staff' },
        { value: '5', label: '5 bookable staff' },
        { value: '6', label: '6 bookable staff' },
        { value: '7', label: '7 bookable staff' },
        { value: '8', label: '8-20 bookable staff' },
        { value: '20', label: '20+ bookable staff' },
    ];
    const defaultValue = options.find(o => o.value == staffCount);
    return (
        <>
            <ReactHint
                position={position}
                attribute="data-plan"
                events={{ click: false, hover: true }}
                onRenderContent={(target, content) => {
                    return (
                        <div
                            className="PlanCalculator-tip react-hint__content"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    );
                }}
            />
            <div data-plan="Bookable staff are anyone in your business that has appointments with clients and admin staff are not billed.">
                <Select
                    className="PlanCalculator-select PlanCalculator-select--large"
                    classNamePrefix="PlanCalculator-select"
                    maxMenuHeight={500}
                    theme={theme => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary75: '#fc5f64',
                            primary50: '#fc5f64',
                        },
                    })}
                    isSearchable={false}
                    onChange={e => setStaffCount(e.value)}
                    defaultValue={defaultValue}
                    value={defaultValue}
                    name="staffCount"
                    options={options}
                />
            </div>
        </>
    );
}
